import type { KeyboardEvent } from "react";
import { Form } from "react-bootstrap";

export type FloatingInputInlineEndVariant = "secondary" | "primary";

interface FloatingInputInlineEndButton {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant: FloatingInputInlineEndVariant;
}

interface FloatingInputProps {
  placeholder: string;
  label: string;
  type: string;
  visiblePassword?: boolean;
  setVisiblePassword?: (next: boolean) => void;
  icon?: React.ComponentType;
  name?: string;
  id?: string;
  value?: string;
  autoComplete?: string;
  required?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  ariaLabel?: string;
  ariaDescribedBy?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** @deprecated Prefer inlineEndButton for sign-in phone/OTP layout */
  onSend?: () => void;
  /** Extra classes on the root `.floatingInput` wrapper (e.g. tone modifiers). */
  rootClassName?: string;
  /** Compact action button flush on the right inside the field border. */
  inlineEndButton?: FloatingInputInlineEndButton;
}

const FloatingInput: React.FC<FloatingInputProps> = ({
  placeholder,
  label,
  icon: Icon,
  type,
  visiblePassword,
  setVisiblePassword,
  name,
  id,
  value,
  autoComplete,
  required,
  readOnly,
  maxLength,
  inputMode,
  ariaLabel,
  ariaDescribedBy,
  onChange,
  onKeyDown,
  onSend,
  rootClassName,
  inlineEndButton,
}) => {
  const inputId = id || name;

  const rootClass = [
    "floatingInput",
    rootClassName,
    inlineEndButton ? "floatingInput--inlineEnd" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (inlineEndButton) {
    return (
      <div className={rootClass}>
        <div className="floatingInput__row">
          <Form.Control
            id={inputId}
            name={name}
            value={value ?? ""}
            autoComplete={autoComplete}
            required={required}
            readOnly={readOnly}
            maxLength={maxLength}
            inputMode={inputMode}
            aria-label={ariaLabel || label}
            aria-describedby={ariaDescribedBy}
            onChange={onChange}
            onKeyDown={onKeyDown}
            className="form-control"
            type={visiblePassword ? "text" : type}
            placeholder={placeholder}
          />
          <button
            type="button"
            className={`floatingInput__endBtn floatingInput__endBtn--${inlineEndButton.variant}`}
            onClick={inlineEndButton.onClick}
            disabled={inlineEndButton.disabled}
          >
            {inlineEndButton.label}
          </button>
        </div>
        <label htmlFor={inputId}>{label}</label>
        {onSend && (
          <button
            type="button"
            className="btn btn-link p-0 small text-decoration-none mt-1 d-block"
            style={{ color: "var(--bs-primary)" }}
            onClick={onSend}
          >
            Send OTP
          </button>
        )}
        {Icon && (
          <>
            {type === "password" ? (
              <span
                className={`fieldIcon  ${visiblePassword ? "active" : ""}`}
                onClick={() => setVisiblePassword?.(!visiblePassword)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setVisiblePassword?.(!visiblePassword);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={visiblePassword ? "Hide password" : "Show password"}
              >
                <Icon />
              </span>
            ) : (
              <span className="fieldIcon">
                <Icon />
              </span>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <Form.Control
        id={inputId}
        name={name}
        value={value ?? ""}
        autoComplete={autoComplete}
        required={required}
        readOnly={readOnly}
        maxLength={maxLength}
        inputMode={inputMode}
        aria-label={ariaLabel || label}
        aria-describedby={ariaDescribedBy}
        onChange={onChange}
        onKeyDown={onKeyDown}
        className="form-control"
        type={visiblePassword ? "text" : type}
        placeholder={placeholder}
      />
      <label htmlFor={inputId}>{label}</label>
      {onSend && (
        <button
          type="button"
          className="btn btn-link p-0 small text-decoration-none mt-1 d-block"
          style={{ color: "var(--bs-primary)" }}
          onClick={onSend}
        >
          Send OTP
        </button>
      )}
      {Icon && (
        <>
          {type === "password" ? (
            <span
              className={`fieldIcon  ${visiblePassword ? "active" : ""}`}
              onClick={() => setVisiblePassword?.(!visiblePassword)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setVisiblePassword?.(!visiblePassword);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={visiblePassword ? "Hide password" : "Show password"}
            >
              <Icon />
            </span>
          ) : (
            <span className="fieldIcon">
              <Icon />
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default FloatingInput;
